import type { DocumentType, WorkflowRun, WorkflowId } from '../../types';
import { apiClient, handleApiError } from '../api-client';

/**
 * Upload a document to a validation
 * @param projectId ID of the project
 * @param documentType Type of document to upload
 * @param file File to upload
 * @returns Updated validation
 */
export const uploadValidationDocument = async (
  projectId: string,
  documentType: DocumentType,
  file: File
): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append('type', documentType);
    formData.append('file', file);

    await apiClient.post(`/projects/${projectId}/documents`, formData);
  } catch (error) {
    return handleApiError(error, 'Failed to upload document');
  }
};

/**
 * Delete a document from a validation
 * @param projectId ID of the project
 * @param documentId ID of the document to delete
 * @returns Updated validation
 */
export const deleteValidationDocument = async (projectId: string, documentId: string): Promise<void> => {
  try {
    await apiClient.delete(`/projects/${projectId}/documents/${documentId}`);
  } catch (error) {
    return handleApiError(error, 'Failed to delete document');
  }
};

/**
 * Re-run a validation
 */
export const startWorkflowRun = async (projectId: string, workflowId: WorkflowId) => {
  try {
    return apiClient.post<WorkflowRun>(`/projects/${projectId}/validate`, { workflowId });
  } catch (error) {
    return handleApiError(error, 'Error re-running validation');
  }
};

/**
 * Stop a running validation
 */
export const stopWorkflowRun = async (projectId: string, workflowId: WorkflowId): Promise<void> => {
  try {
    await apiClient.post(`/projects/${projectId}/stop`, { workflowId });
  } catch (error) {
    return handleApiError(error, 'Error stopping validation');
  }
};

/**
 * Get the latest workflow run for a project and workflowId
 * @returns The run, or null if the project has none (e.g. EPD matching is disabled)
 */
export const getWorkflowRun = async <T extends WorkflowId>(
  projectId: string,
  workflowId: T
): Promise<WorkflowRun<T> | null> => {
  try {
    return await apiClient.get<WorkflowRun<T>>(`/projects/${projectId}/workflowruns/${workflowId}`);
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
      return null;
    }
    return handleApiError(error, 'Failed to load workflow run');
  }
};
